import React, { useContext, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSitemap, faFileAlt } from '@fortawesome/free-solid-svg-icons'
import UserContext from 'context/user-context'
import { useUnlinkReport, useRegenerateReportText } from 'api/hooks/use-report-builder'
import { useFolderContext, withFolderContext } from 'dashboard/folders/folder-context'

// Shown at the top of a child report. A child renders its parent's layout over one slice of the
// data, so there is nothing here to edit — the banner says so, points at the parent, and offers
// the one action that changes that.
const ChildReportBanner = ({ report }) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useContext(UserContext)
  const [showConfirm, setShowConfirm] = useState(false)
  // A child is filed with its parent, so hopping to the parent stays inside the same folder — the
  // trail back to it comes along rather than being dropped on the way up.
  const folderId = useFolderContext()

  // Mirrors ReportPolicy#unlink? — a read-only viewer would otherwise see the button and get a
  // bare "Could not unlink the report" toast off a 403. Edit access can come from the template's
  // share list rather than the child's own, which is what editor_via_parent carries.
  const isOwner = report?.user_id === user?.id
  const isEditor = report?.shared_users?.find((ru) => ru.user_id === user?.id)?.is_editor ?? false
  const canUnlink = (isOwner || isEditor || !!report?.editor_via_parent) && !report?.locked

  const { mutate: unlinkReport, isLoading: isUnlinking } = useUnlinkReport({ options: {} })
  const { mutate: regenerateText, isLoading: isRegenerating } = useRegenerateReportText({ options: {} })

  // A child's charts are its own the moment it exists, but its narrative is the parent's until it
  // has been rewritten — so prose quoting the parent's totals sits above the child's figures.
  // Saying so is the difference between a caveat and a wrong audit finding.
  const text = report?.text_regeneration
  const narrativePending = (text?.pending || 0) > 0
  const narrativeStale = (text?.stale || 0) > 0
  const narrativeFailed = (text?.failed || 0) > 0

  const handleRegenerate = () => {
    regenerateText(report.id, {
      onSuccess: () => {
        queryClient.invalidateQueries([`reports/${report.id}`])
        toast.success('Rewriting this report narrative — reload in a moment')
      },
      onError: () => toast.error('Could not start the rewrite')
    })
  }

  const parent = report?.parent_report
  // A colleague the child was shared with has no access to the parent, so the API withholds its
  // id and name (reports/_report.json.jbuilder). Naming it anyway would be a link into a 403.
  const parentAccessible = !!parent?.accessible
  const parentLabel = parentAccessible ? (parent?.name || 'the parent report') : 'the report it was split from'
  const valueLabel = report?.split?.value_label

  const handleUnlink = () => {
    unlinkReport(report.id, {
      onSuccess: () => {
        queryClient.invalidateQueries([`reports/${report.id}`])
        queryClient.invalidateQueries([`reports/${report.id}/report_grid_rows`])
        queryClient.invalidateQueries([`reports/${report.id}/report_charts`])
        queryClient.invalidateQueries([`reports/${report.id}/report_datasets`])
        queryClient.invalidateQueries(['reports-builder'])
        setShowConfirm(false)
        toast.success('Report unlinked — you can now edit it')
        navigate(0)
      },
      onError: () => toast.error('Could not unlink the report')
    })
  }

  return (
    <>
      <Alert variant="warning" className="d-flex align-items-center gap-3 py-2">
        <FontAwesomeIcon icon={faSitemap} className="text-warning" />
        <div className="flex-grow-1">
          <strong>Child report{valueLabel ? ` — ${valueLabel}` : ''}.</strong>{' '}
          Its layout is controlled by{' '}
          {parentAccessible
            ? <Link to={withFolderContext(`/reports/${parent.id}`, folderId)}>{parentLabel}</Link>
            : parentLabel}
          {' '}and can&apos;t be edited here. Changes to the parent appear here automatically.
        </div>
        {canUnlink && (
          <Button variant="light" className="text-primary border" onClick={() => setShowConfirm(true)}>
            Unlink report
          </Button>
        )}
      </Alert>

      {(narrativePending || narrativeStale || narrativeFailed) && (
        <Alert variant={narrativeFailed ? 'danger' : 'warning'} className="d-flex align-items-center gap-3 py-2">
          <FontAwesomeIcon icon={faFileAlt} className={narrativeFailed ? 'text-danger' : 'text-warning'} />
          <div className="flex-grow-1">
            {narrativePending && (
              <>
                <strong>Narrative is being rewritten.</strong> Until it finishes, the written
                sections still quote the whole-population figures, not {valueLabel || 'this slice'}
                &apos;s.
              </>
            )}
            {!narrativePending && narrativeFailed && (
              <>
                <strong>{text.failed} written section{text.failed === 1 ? '' : 's'} could not be
                rewritten.</strong>{' '}
                {text.failed === 1 ? 'It still quotes' : 'They still quote'} the whole-population
                figures.
              </>
            )}
            {!narrativePending && !narrativeFailed && narrativeStale && (
              <>
                <strong>Narrative is out of date.</strong> The template&apos;s written sections have
                changed since this copy was rewritten.
              </>
            )}
          </div>
          {canUnlink && !narrativePending && (
            <Button
              variant="light"
              size="sm"
              className="text-primary border"
              onClick={handleRegenerate}
              disabled={isRegenerating}
            >
              {isRegenerating ? 'Starting…' : 'Rewrite narrative'}
            </Button>
          )}
        </Alert>
      )}

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5">Unlink this report?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            It will keep the data it shows now and become editable on its own.
          </p>
          <p className="mb-0 text-muted">
            It will stop following <strong>{parentLabel}</strong>, so later changes to the parent
            won&apos;t reach it. This can&apos;t be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light-primary" onClick={() => setShowConfirm(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleUnlink} disabled={isUnlinking}>
            {isUnlinking ? 'Unlinking…' : 'Yes, unlink'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ChildReportBanner
